import * as d3 from 'd3';
import { Layer, TimeUnit } from '../types';
import { TIME_UNITS, getLayerTimeUnit, getTimeUnitLabel, isTimeValueLayer } from './layerUtils';

interface AttributeEditModalOptions {
  layer: Layer;
  title: string;
  onSave: (value: string, timeUnit?: TimeUnit) => void;
  onCancel?: () => void;
  t: (key: string) => string;
}

export function createAttributeEditModal(
  options: AttributeEditModalOptions
): void {
  const { layer, title, onSave, onCancel, t } = options;

  // Remove any existing modal
  const existingModal = document.querySelector('.attribute-edit-modal-overlay');
  if (existingModal) {
    existingModal.remove();
  }

  // Create overlay
  const overlay = d3.select('body')
    .append('div')
    .attr('class', 'attribute-edit-modal-overlay')
    .style('position', 'fixed')
    .style('top', '0')
    .style('left', '0')
    .style('width', '100%')
    .style('height', '100%')
    .style('background-color', 'rgba(0, 0, 0, 0.5)')
    .style('display', 'flex')
    .style('align-items', 'center')
    .style('justify-content', 'center')
    .style('z-index', '10000');

  // Create modal container
  const modal = overlay
    .append('div')
    .attr('class', 'attribute-edit-modal')
    .style('background', '#ffffff')
    .style('border-radius', '8px')
    .style('box-shadow', '0 10px 25px rgba(0, 0, 0, 0.2)')
    .style('width', 'auto')
    .style('max-width', '500px')
    .style('min-width', '300px')
    .style('max-height', '90vh')
    .style('display', 'flex')
    .style('flex-direction', 'column')
    .style('overflow', 'hidden');

  // Create header
  const header = modal
    .append('div')
    .style('padding', '12px 16px')
    .style('border-bottom', '1px solid #e5e7eb')
    .style('display', 'flex')
    .style('justify-content', 'space-between')
    .style('align-items', 'center');

  header
    .append('h5')
    .style('margin', '0')
    .style('font-size', '16px')
    .style('font-weight', '600')
    .style('color', '#1f2937')
    .text(title);

  const closeButton = header
    .append('button')
    .style('background', 'none')
    .style('border', 'none')
    .style('font-size', '24px')
    .style('cursor', 'pointer')
    .style('color', '#6b7280')
    .style('padding', '0')
    .style('width', '32px')
    .style('height', '32px')
    .style('display', 'flex')
    .style('align-items', 'center')
    .style('justify-content', 'center')
    .style('border-radius', '4px')
    .text('×')
    .on('mouseenter', function() {
      d3.select(this).style('background', '#f3f4f6').style('color', '#1f2937');
    })
    .on('mouseleave', function() {
      d3.select(this).style('background', 'none').style('color', '#6b7280');
    })
    .on('click', () => {
      if (onCancel) onCancel();
      overlay.remove();
    });

  // Create body
  const body = modal
    .append('div')
    .style('padding', '16px')
    .style('flex', '1')
    .style('overflow-y', 'auto')
    .style('overflow-x', 'hidden')
    .style('min-height', '0'); // Permet au flex de fonctionner correctement

  // Create label
  const label = body
    .append('label')
    .style('display', 'block')
    .style('margin-bottom', '8px')
    .style('font-weight', '500')
    .style('color', '#374151')
    .style('font-size', '14px')
    //.text(layer.text);

  // Toujours utiliser un textarea pour tous les types d'attributs avec auto-resize
  const currentValue = String(layer.value || '');

  let inputElement: HTMLInputElement | HTMLTextAreaElement;

  // Utiliser un textarea pour TOUS les types d'attributs
  {
    // Utiliser un textarea pour tous les types d'attributs avec auto-resize
    const textarea = body
      .append('textarea')
      .attr('class', 'attribute-edit-input')
      .property('value', currentValue)
      .style('width', '100%')
      .style('min-height', '40px')
      .style('padding', '10px')
      .style('border', '1px solid #d1d5db')
      .style('border-radius', '6px')
      .style('font-size', '14px')
      .style('color', '#1f2937')
      .style('outline', 'none')
      .style('box-sizing', 'border-box')
      .style('resize', 'none')
      .style('overflow-y', 'hidden')
      .style('overflow-x', 'hidden')
      .style('font-family', 'inherit')
      .style('word-wrap', 'break-word')
      .style('word-break', 'break-word')
      .style('white-space', 'pre-wrap')
      .style('line-height', '1.5')
      .on('input', function() {
        const target = this as HTMLTextAreaElement;
        // Réinitialiser la hauteur pour forcer le recalcul
        const currentHeight = target.style.height;
        target.style.height = '0px';
        // Forcer le recalcul du layout
        void target.offsetHeight;
        // Calculer la nouvelle hauteur basée sur scrollHeight
        const newHeight = Math.max(target.scrollHeight, 40);
        target.style.height = `${newHeight}px`;
      })
      .on('focus', function() {
        d3.select(this).style('border-color', '#6366f1');
        const target = this as HTMLTextAreaElement;
        target.style.height = 'auto';
        const newHeight = Math.max(target.scrollHeight, 40);
        target.style.height = `${newHeight}px`;
      })
      .on('blur', function() {
        d3.select(this).style('border-color', '#d1d5db');
      })
      .on('copy', (event: ClipboardEvent) => {
        event.stopPropagation();
      })
      .on('paste', (event: ClipboardEvent) => {
        event.stopPropagation();
        // Auto-resize après le paste
        setTimeout(() => {
          const target = event.target as HTMLTextAreaElement;
          if (target) {
            target.style.height = 'auto';
            const newHeight = Math.max(target.scrollHeight, 40);
            target.style.height = `${newHeight}px`;
          }
        }, 0);
      })
      .on('cut', (event: ClipboardEvent) => {
        event.stopPropagation();
        // Auto-resize après le cut
        setTimeout(() => {
          const target = event.target as HTMLTextAreaElement;
          if (target) {
            target.style.height = 'auto';
            const newHeight = Math.max(target.scrollHeight, 40);
            target.style.height = `${newHeight}px`;
          }
        }, 0);
      });
    
    inputElement = textarea.node() as HTMLTextAreaElement;
    
    // Initialiser la hauteur au chargement
    const initHeight = () => {
      const node = textarea.node() as HTMLTextAreaElement;
      if (node) {
        // Réinitialiser complètement
        node.style.height = '0px';
        // Forcer le recalcul du layout
        void node.offsetHeight;
        // Calculer la nouvelle hauteur basée sur scrollHeight
        const newHeight = Math.max(node.scrollHeight, 40);
        node.style.height = `${newHeight}px`;
      }
    };
    
    // Initialiser plusieurs fois pour s'assurer que ça fonctionne après le rendu
    requestAnimationFrame(() => {
      initHeight();
    });
    setTimeout(() => {
      initHeight();
    }, 10);
    setTimeout(() => {
      initHeight();
    }, 100);
  }

  // Add hint for link type
  if (layer.type === 'link') {
    body
      .append('p')
      .style('margin-top', '8px')
      .style('margin-bottom', '0')
      .style('font-size', '12px')
      .style('color', '#6b7280')
      .text(t('layer.enterUrl') || 'Enter a valid URL');
  }

  let selectedTimeUnit = getLayerTimeUnit(layer);

  if (isTimeValueLayer(layer)) {
    const unitRow = body
      .append('div')
      .style('margin-top', '12px')
      .style('display', 'flex')
      .style('align-items', 'center')
      .style('gap', '8px');

    unitRow
      .append('label')
      .style('font-size', '14px')
      .style('font-weight', '500')
      .style('color', '#374151')
      .style('white-space', 'nowrap')
      .text(t('layer.timeUnit') || 'Unit');

    const unitSelect = unitRow
      .append('select')
      .style('padding', '6px 8px')
      .style('border', '1px solid #d1d5db')
      .style('border-radius', '6px')
      .style('font-size', '14px')
      .style('color', '#1f2937')
      .style('background', '#fff')
      .style('cursor', 'pointer');

    TIME_UNITS.forEach((unit) => {
      unitSelect
        .append('option')
        .attr('value', unit)
        .property('selected', unit === selectedTimeUnit)
        .text(getTimeUnitLabel(unit, t));
    });

    unitSelect.on('change', function () {
      selectedTimeUnit = this.value as TimeUnit;
    });
  }

  // Create footer
  const footer = modal
    .append('div')
    .style('padding', '12px 16px')
    .style('border-top', '1px solid #e5e7eb')
    .style('display', 'flex')
    .style('justify-content', 'flex-end')
    .style('gap', '12px');

  const cancelBtn = footer
    .append('button')
    .style('padding', '8px 16px')
    .style('background', '#f3f4f6')
    .style('border', '1px solid #d1d5db')
    .style('border-radius', '6px')
    .style('cursor', 'pointer')
    .style('color', '#374151')
    .style('font-size', '14px')
    .style('font-weight', '500')
    .text(t('words.cancel') || 'Cancel')
    .on('mouseenter', function() {
      d3.select(this).style('background', '#e5e7eb');
    })
    .on('mouseleave', function() {
      d3.select(this).style('background', '#f3f4f6');
    })
    .on('click', () => {
      if (onCancel) onCancel();
      overlay.remove();
    });

  const saveBtn = footer
    .append('button')
    .style('padding', '8px 16px')
    .style('background', '#6366f1')
    .style('border', 'none')
    .style('border-radius', '6px')
    .style('cursor', 'pointer')
    .style('color', '#ffffff')
    .style('font-size', '14px')
    .style('font-weight', '500')
    .text(t('words.save') || 'Save')
    .on('mouseenter', function() {
      d3.select(this).style('background', '#4f46e5');
    })
    .on('mouseleave', function() {
      d3.select(this).style('background', '#6366f1');
    })
    .on('click', () => {
      const value = (inputElement as HTMLTextAreaElement).value;
      onSave(value, isTimeValueLayer(layer) ? selectedTimeUnit : undefined);
      overlay.remove();
    });

  // Handle click outside modal
  overlay.on('click', function(event) {
    if (event.target === overlay.node()) {
      if (onCancel) onCancel();
      overlay.remove();
    }
  });

  // Handle Escape key
  const escapeHandler = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      if (onCancel) onCancel();
      overlay.remove();
      document.removeEventListener('keydown', escapeHandler);
    }
  };
  document.addEventListener('keydown', escapeHandler);

  // Focus textarea (toujours un textarea maintenant)
  requestAnimationFrame(() => {
    if (inputElement && inputElement instanceof HTMLTextAreaElement) {
      inputElement.focus();
      // Placer le curseur à la fin
      inputElement.setSelectionRange(inputElement.value.length, inputElement.value.length);
    }
  });

  // Handle Enter key (Ctrl+Enter pour sauvegarder dans textarea)
  // Puisque nous utilisons toujours un textarea maintenant
  d3.select(inputElement).on('keydown', (event: KeyboardEvent) => {
    if (event.key === 'Enter' && event.ctrlKey) {
      event.preventDefault();
      const value = (inputElement as HTMLTextAreaElement).value;
      onSave(value, isTimeValueLayer(layer) ? selectedTimeUnit : undefined);
      overlay.remove();
      document.removeEventListener('keydown', escapeHandler);
    }
  });
}
